import { useEffect, useState } from 'react';
import { contract, PlaceTypeKeyEnum } from '@aviv_price-pages/brand';
import { useLocale } from '@aviv-unified-frontend/runtime/i18n';
import { v4 as uuid } from 'uuid';
import HttpClient from '@/adapters/http/HttpClient';
import { DEFAULT_AUTOCOMPLETE_LOADING_TIMEOUT_MS } from '@/constants';
import { MediumCta } from '@/entities/enums/MediumCta';
import { debouncePromise } from '@/helpers/debouncePromise';
import { getPlaceSearchMediumAttributeFunction } from '@/helpers/tracking/tracking';
import { usePlaceNavigation } from '@/hooks/usePlaceNavigation/usePlaceNavigation';
const DEFAULT_PLACEKEYS = [
    PlaceTypeKeyEnum.REGION,
    PlaceTypeKeyEnum.PROVINCE,
    PlaceTypeKeyEnum.MICROREGION,
    PlaceTypeKeyEnum.MUNICIPALITY,
    PlaceTypeKeyEnum.BOROUGH,
    PlaceTypeKeyEnum.NEIGHBORHOOD,
    PlaceTypeKeyEnum.MICRO_STAT_UNIT,
    PlaceTypeKeyEnum.STREET,
    PlaceTypeKeyEnum.HOUSE_NUMBER,
];
const DEFAULT_PARENT_TYPES = [
    PlaceTypeKeyEnum.STREET,
    PlaceTypeKeyEnum.MUNICIPALITY,
    PlaceTypeKeyEnum.HOUSE_NUMBER,
    PlaceTypeKeyEnum.NEIGHBORHOOD,
    PlaceTypeKeyEnum.PROVINCE,
];
export var FeedbackSubtype;
(function (FeedbackSubtype) {
    FeedbackSubtype["RESET"] = "RESET";
    FeedbackSubtype["COMPLETED"] = "COMPLETED";
    FeedbackSubtype["EXIT"] = "EXIT";
})(FeedbackSubtype || (FeedbackSubtype = {}));
/**
 * Hook to manage search autocomplete input and search results
 *
 * @param placeTypes - List of allowed place types
 * @param parentsTypes - List of allowed parents for places
 * @returns Object containing state and callbacks used by `Searchbox` component
 */
export function usePlaceSearch(placeTypes = DEFAULT_PLACEKEYS, parentsTypes = [
    ...DEFAULT_PARENT_TYPES,
    ...(contract.urls?.parentsTypeForAutoComplete ?? []),
]) {
    const { language } = useLocale();
    const initialState = {
        search: '',
        places: [],
        isLoading: false,
        error: undefined,
        searchUnderlayOpen: false,
    };
    const [state, setState] = useState(initialState);
    const { navigateToPlace } = usePlaceNavigation(contract.nameAbbreviation, getPlaceSearchMediumAttributeFunction(MediumCta.AUTO_COMPLETE));
    useEffect(() => {
        sessionStorage.setItem('autocompleteSessionId', uuid());
    }, []);
    const onFocusChange = (open) => {
        setState((prevState) => {
            sendFeedback(prevState.search || '', FeedbackSubtype.RESET);
            return {
                ...prevState,
                search: '',
                searchUnderlayOpen: open,
            };
        });
    };
    const sendFeedback = (input, completed, position, placeId) => {
        if (!input) {
            return;
        }
        const positionValue = position === undefined ? '' : `&position=${position}`;
        const placeIdValue = placeId ? `&placeId=${placeId}` : '';
        const sessionId = sessionStorage.getItem('autocompleteSessionId');
        const url = `${process.env.BFF_BASE_URL}/search/feedback?input=${input}&completed=${completed}&sessionId=${sessionId}${positionValue}${placeIdValue}`;
        HttpClient.getInstance().get(url, undefined, process.env.BFF_API_KEY);
    };
    const onSelectionChange = (place) => {
        setState(initialState);
        sessionStorage.removeItem('hasFiredSidePanelAgencyCardViewItemListTracking');
        navigateToPlace(place);
    };
    const handleChange = async (value) => {
        setState((prevState) => {
            if (prevState.search && !value) {
                sendFeedback(prevState.search, FeedbackSubtype.RESET);
            }
            return {
                ...prevState,
                search: value,
                searchUnderlayOpen: true,
                error: undefined,
            };
        });
        if (!value) {
            return;
        }
        if (!state.search) {
            setState((prevState) => ({
                ...prevState,
                isLoading: true,
            }));
        }
        try {
            const sessionId = sessionStorage.getItem('autocompleteSessionId');
            const url = `${process.env.BFF_BASE_URL}/search?input=${value}&placeTypes=${placeTypes.join(',')}&parentTypes=${parentsTypes.join(',')}&language=${language}&sessionId=${sessionId}`;
            const places = await debouncePromise(() => HttpClient.getInstance().get(url, undefined, process.env.BFF_API_KEY), DEFAULT_AUTOCOMPLETE_LOADING_TIMEOUT_MS);
            if (value === 'test') {
                throw new Error();
            }
            setState((prevState) => ({
                ...prevState,
                places,
                isLoading: false,
            }));
        }
        catch (error) {
            setState((prevstate) => ({
                ...prevstate,
                places: [],
                search: value,
                error: error,
                isLoading: false,
            }));
        }
    };
    return { state, handleChange, onFocusChange, onSelectionChange, sendFeedback };
}
